import { Link } from 'react-router-dom';

const values = [
  { icon: 'ri-shield-check-line', title: 'Integrity', desc: 'We\'re honest and upfront - with our clients, our people, and ourselves.' },
  { icon: 'ri-medal-line', title: 'Reliability', desc: 'We will be there when you call with the right people.' },
  { icon: 'ri-heart-line', title: 'People First', desc: 'We take care of our people so they take care of your business.' },
];

export default function AboutPage() {
  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="bg-[#1a3a3a] py-24 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full overflow-hidden pointer-events-none">
          <div
            className="absolute top-0 right-0 w-full h-full opacity-10"
            style={{ background: 'repeating-linear-gradient(135deg, #c8a84b 0px, #c8a84b 2px, transparent 2px, transparent 24px)' }}
          />
        </div>
        <div className="absolute top-0 left-0 w-1 h-full bg-[#c8a84b]" />
        <div className="max-w-6xl mx-auto px-8 md:px-14">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-8 bg-[#c8a84b]" />
            <span className="text-[#c8a84b] text-xs font-bold tracking-[0.3em] uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>Our Story</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6 leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            About Us
          </h1>
          <p className="text-white/65 text-lg leading-relaxed max-w-2xl" style={{ fontFamily: "'Inter', sans-serif" }}>
            Our business is built on relationships and results.
          </p>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-20 bg-[#f8f7f2]">
        <div className="max-w-6xl mx-auto px-8 md:px-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="flex items-start gap-5 mb-8">
                <div className="w-1 bg-[#c8a84b] self-stretch rounded-full shrink-0" style={{ minHeight: '50px' }} />
                <div>
                  <p className="text-[#c8a84b] text-xs font-bold tracking-[0.3em] uppercase mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>Who We Are</p>
                  <h2 className="text-4xl font-extrabold text-[#1a3a3a] leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    We Exist to Make Staffing Less Stressful
                  </h2>
                </div>
              </div>
              <div className="space-y-5 text-gray-500 text-base leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                <p>
                  We know what it is like to need reliable people fast. Employees have emergencies, get sick, quit, and sometimes just don&apos;t show up. Finding people to fill in is stressful, especially after hours.
                </p>
                <p>
                  We built our business to make finding temporary help less stressful. We do that by knowing what you need for a specific role and vetting our people to ensure they fit. Finding a warm body is easy. Finding a competent person capable of filling a specific role is much harder. Our extensive vetting and training ensures that we have the right person ready to go when you call us.
                </p>
                <p>
                  Our business is based on relationships with the companies we serve. We survive based on the results we deliver. Give us a chance, and we will show you what we can do.
                </p>
              </div>
              <div className="mt-8">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-[#c8a84b] text-white text-sm font-bold tracking-wide hover:bg-[#b8943b] transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Work With Us <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>

            {/* Quote + accent panel */}
            <div className="flex flex-col gap-6">
              <div className="bg-[#1a3a3a] rounded-lg p-10">
                <i className="ri-double-quotes-l text-3xl text-[#c8a84b] mb-4 block" />
                <p className="text-white text-lg italic leading-relaxed mb-6" style={{ fontFamily: "'Georgia', serif" }}>
                  Finding a warm body is easy. Finding a competent person capable of filling a specific role is much harder.
                </p>
                <div className="h-px bg-white/15 mb-6" />
                <p className="text-white/60 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Our extensive vetting and training ensures that we have the right person ready to go when you call us.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { icon: 'ri-timer-flash-line', label: 'Fast Placement' },
                  { icon: 'ri-shield-check-line', label: 'Pre-Screened Candidates' },
                  { icon: 'ri-heart-line', label: 'People First' },
                ].map((item) => (
                  <div key={item.label} className="bg-white rounded-lg p-6 text-center border border-gray-100 flex flex-col items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full" style={{ background: 'rgba(26,58,58,0.08)' }}>
                      <i className={`${item.icon} text-xl text-[#c8a84b]`} />
                    </div>
                    <div className="text-xs text-gray-500 font-medium leading-snug" style={{ fontFamily: "'Inter', sans-serif" }}>{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-8 md:px-14">
          <div className="flex items-start gap-5 mb-14">
            <div className="w-1 bg-[#c8a84b] self-stretch rounded-full shrink-0" style={{ minHeight: '50px' }} />
            <div>
              <p className="text-[#c8a84b] text-xs font-bold tracking-[0.3em] uppercase mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>What Drives Us</p>
              <h2 className="text-4xl font-extrabold text-[#1a3a3a] leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>Our Values</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-100 rounded-lg overflow-hidden">
            {values.map((v) => (
              <div key={v.title} className="bg-white p-10 flex flex-col items-center text-center group hover:bg-[#f8f7f2] transition-colors duration-200">
                <div className="w-14 h-14 flex items-center justify-center rounded-full border-2 border-[#1a3a3a]/15 mb-6 group-hover:border-[#c8a84b] transition-colors duration-200">
                  <i className={`${v.icon} text-2xl text-[#1a3a3a] group-hover:text-[#c8a84b] transition-colors duration-200`} />
                </div>
                <h3 className="text-base font-extrabold text-[#1a3a3a] mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1a3a3a] text-center">
        <div className="max-w-3xl mx-auto px-8">
          <h2 className="text-4xl font-extrabold text-white mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Ready to Work Together?
          </h2>
          <p className="text-white/65 text-lg mb-10" style={{ fontFamily: "'Inter', sans-serif" }}>
            Give us a chance, and we will show you what we can do.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-md bg-[#c8a84b] text-white font-bold text-sm tracking-wide hover:bg-[#b8943b] transition-colors duration-200 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Contact Us Today <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>
    </main>
  );
}
